/**
 * API fetchers for CRM Recruitments
 * Base path: /api/v5/agencies/:agency_id/crm/recruitments
 */
import axiosWithAuth from '@/utils/axiosWithAuth';
import type {
  ListRecruitmentsParams,
  ApiRecruitmentsResponse,
  BulkEmailParams,
  BulkEmailResponse,
  BulkRemoveRecruitmentsParams,
  BulkRemoveRecruitmentsResponse,
  BulkThreadsParams,
  BulkThreadsResponse,
  BulkRequestVettingParams,
  BulkRequestVettingResponse,
  RemoveRecruitmentParams,
} from '@/views/creators/recruit-crm/types';
import api from '@/api';

const recruitmentsFetchers = {
  /**
   * List recruitments with filtering and pagination
   * GET /api/v5/agencies/:agency_id/crm/recruitments
   */
  list: (params: ListRecruitmentsParams): Promise<ApiRecruitmentsResponse> => {
    const {agencyId, status, sortBy, sortOrder, page, perPage} = params;

    return api.get<ApiRecruitmentsResponse>(
      `/agencies/${agencyId}/crm/recruitments`,
      {
        params: {
          ...(status ? {status} : {}),
          ...(sortBy ? {sort_by: sortBy} : {}),
          ...(sortOrder ? {sort_order: sortOrder} : {}),
          page,
          per_page: perPage,
        },
      }
    ) as unknown as Promise<ApiRecruitmentsResponse>;
  },

  /**
   * Remove a recruitment (soft delete)
   * PATCH /api/v5/agencies/:agency_id/crm/recruitments/:recruitment_id/remove
   */
  remove: (params: RemoveRecruitmentParams) => {
    const {recruitmentId, agencyId} = params;

    return api.patch(
      `/agencies/${agencyId}/crm/recruitments/${recruitmentId}/remove`,
      {},
      {params: {}}
    );
  },

  /**
   * Bulk remove recruitments (soft delete)
   * POST /api/v5/agencies/:agency_id/crm/recruitments/bulk_remove
   */
  bulkRemove: async (
    params: BulkRemoveRecruitmentsParams
  ): Promise<BulkRemoveRecruitmentsResponse> => {
    const {agencyId, crmRecruitmentIds} = params;

    const result = await axiosWithAuth.post<BulkRemoveRecruitmentsResponse>(
      `/agencies/${agencyId}/crm/recruitments/bulk_remove`,
      {
        crm_recruitment_ids: crmRecruitmentIds,
      }
    );
    return result.data;
  },

  /**
   * Send bulk emails to recruitments
   * POST /api/v5/agencies/:agency_id/crm/recruitments/bulk_emails
   */
  bulkEmails: async (params: BulkEmailParams): Promise<BulkEmailResponse> => {
    const {agencyId, crmRecruitmentIds, subject, body} = params;

    const result = await axiosWithAuth.post<BulkEmailResponse>(
      `/agencies/${agencyId}/crm/recruitments/bulk_emails`,
      {
        crm_recruitment_ids: crmRecruitmentIds,
        subject,
        body,
      }
    );
    return result.data;
  },

  /**
   * Create bulk threads with recruitments (internal creators only)
   * POST /api/v5/agencies/:agency_id/crm/recruitments/bulk_threads
   */
  bulkThreads: async (
    params: BulkThreadsParams
  ): Promise<BulkThreadsResponse> => {
    const {agencyId, crmRecruitmentIds, brandId, initialMessage} = params;

    const result = await axiosWithAuth.post<BulkThreadsResponse>(
      `/agencies/${agencyId}/crm/recruitments/bulk_threads`,
      {
        crm_recruitment_ids: crmRecruitmentIds,
        brand_id: brandId,
        initial_message: initialMessage,
      }
    );
    return result.data;
  },

  /**
   * Request vetting for multiple recruitments
   * POST /api/v5/agencies/:agency_id/crm/recruitments/bulk_vetting
   */
  bulkRequestVetting: async (
    params: BulkRequestVettingParams
  ): Promise<BulkRequestVettingResponse> => {
    const {agencyId, crmRecruitmentIds, brandId} = params;

    const result = await axiosWithAuth.post<BulkRequestVettingResponse>(
      `/agencies/${agencyId}/crm/recruitments/bulk_vetting`,
      {
        crm_recruitment_ids: crmRecruitmentIds,
        brand_id: brandId,
      }
    );
    return result.data;
  },
};

export default recruitmentsFetchers;
